import {
  Check, Clock, Loader2, Plus, X,
} from 'lucide-react';

import SearchableSelect from '@/components/common/SearchableSelect';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { CURRENCIES, type OfferPackage } from '@/types/offer';

export type OfferDraft = {
  title: string;
  introduction: string;
  currency: string;
  billing_mode: 'direct' | 'via_aoc';
  packages: OfferPackage[];
};

export const emptyPackage = (): OfferPackage => ({
  name: '', summary: '', monthly_fee: '', services: [], hourly_rates: [],
});

// An offer is written in the money the client is invoiced in. Anything else
// cannot go on their invoice -- billing refuses the whole organisation rather
// than mixing currencies on one document -- so the advisor starts in the right
// one and can still change it.
export const emptyDraft = (currency = 'EUR'): OfferDraft => ({
  title: '', introduction: '', currency, billing_mode: 'direct', packages: [emptyPackage()],
});

export function draftIsComplete(draft: OfferDraft) {
  return Boolean(draft.title.trim()) && draft.packages.every((p) => p.name.trim() && p.monthly_fee);
}

export default function OfferEditor({
  draft, setDraft, onSave, onCancel, saving, feeLabel, heading = 'A new offer', saveLabel = 'Save as draft',
}: {
  draft: OfferDraft;
  setDraft: (next: OfferDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  feeLabel: string;
  heading?: string;
  saveLabel?: string;
}) {
  const setPackage = (index: number, patch: Partial<OfferPackage>) =>
    setDraft({
      ...draft,
      packages: draft.packages.map((p, i) => (i === index ? { ...p, ...patch } : p)),
    });

  return (
    <Card data-testid="offer-editor">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">{heading}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Title</label>
            <Input
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="Automation support, 2026"
              data-testid="offer-title"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Currency</label>
            <SearchableSelect
              value={draft.currency}
              onChange={(value) => setDraft({ ...draft, currency: value })}
              placeholder="Currency"
              searchPlaceholder="Search currencies…"
              options={CURRENCIES.map((c) => ({ value: c, label: c }))}
              testId="offer-currency"
            />
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-muted-foreground">Who sends the invoices</label>
          <div className="mt-1 grid gap-2 sm:grid-cols-2">
            {([
              ['direct', 'I invoice them directly', 'You send your own invoices each month. BitSwan tracks what is owed and when you sent them.', 'No platform fee'],
              ['via_aoc', 'BitSwan invoices them', 'Your package and hours go on their monthly BitSwan invoice. Once they pay, you request a payout.', feeLabel],
            ] as const).map(([mode, label, help, fee]) => (
              <button
                key={mode}
                type="button"
                onClick={() => setDraft({ ...draft, billing_mode: mode })}
                className={`rounded-lg border p-3 text-left text-sm ${draft.billing_mode === mode ? 'border-primary ring-1 ring-primary' : 'hover:border-primary/50'}`}
                data-testid={`billing-mode-${mode}`}
              >
                <span className="flex items-start justify-between gap-2">
                  <span className="font-medium">{label}</span>
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      mode === 'direct'
                        ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
                        : 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
                    }`}
                    data-testid={`billing-mode-fee-${mode}`}
                  >
                    {fee}
                  </span>
                </span>
                <span className="mt-0.5 block text-xs text-muted-foreground">{help}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-muted-foreground">
            What this offer is for
          </label>
          <textarea
            className="min-h-[80px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={draft.introduction}
            onChange={(e) => setDraft({ ...draft, introduction: e.target.value })}
            placeholder="Describe what this offer covers."
            data-testid="offer-introduction"
          />
        </div>

        <div className="space-y-3">
          {draft.packages.map((pkg, index) => (
            <div
              key={index}
              className="space-y-3 rounded-lg border p-3"
              data-testid={`package-editor-${index}`}
            >
              <div className="flex items-start gap-2">
                <div className="grid flex-1 gap-2 sm:grid-cols-[1fr_220px]">
                  <Input
                    value={pkg.name}
                    onChange={(e) => setPackage(index, { name: e.target.value })}
                    placeholder="Package name (e.g. Standard)"
                    data-testid={`package-name-${index}`}
                  />
                  <AmountField
                    unit={`${draft.currency} / month`}
                    value={pkg.monthly_fee}
                    onChange={(value) => setPackage(index, { monthly_fee: value })}
                    placeholder="Monthly fee"
                    testId={`package-fee-${index}`}
                  />
                </div>
                {draft.packages.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDraft({
                      ...draft,
                      packages: draft.packages.filter((_, i) => i !== index),
                    })}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <Lines
                label="What it includes"
                placeholder="Monitoring, 12 hours of support included…"
                testIdPrefix={`service-${index}`}
                values={pkg.services.map((s) => s.text)}
                onChange={(texts) =>
                  setPackage(index, { services: texts.map((text) => ({ text })) })}
              />

              <RateLines
                testIdPrefix={`rate-${index}`}
                currency={draft.currency}
                rates={pkg.hourly_rates}
                onChange={(hourly_rates) => setPackage(index, { hourly_rates })}
              />
            </div>
          ))}

          <Button
            variant="outline"
            size="sm"
            onClick={() => setDraft({
              ...draft, packages: [...draft.packages, emptyPackage()],
            })}
            data-testid="add-package-btn"
          >
            <Plus className="mr-2 h-3.5 w-3.5" />
            Another package to choose between
          </Button>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={() => onSave()}
            disabled={
              saving
              || !draft.title.trim()
              || draft.packages.some((p) => !p.name.trim() || !p.monthly_fee)
            }
            data-testid="save-offer-btn"
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {saveLabel}
          </Button>
          <Button variant="ghost" onClick={() => onCancel()}>Cancel</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Lines({
  label, placeholder, values, onChange, testIdPrefix,
}: {
  label: string;
  placeholder: string;
  values: string[];
  onChange: (values: string[]) => void;
  testIdPrefix: string;
}) {
  const rows = [...values, ''];
  return (
    <div className="space-y-1.5">
      <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Check className="h-3.5 w-3.5" />
        {label}
      </p>
      {rows.map((value, index) => (
        <Input
          key={index}
          className="h-8 text-sm"
          value={value}
          placeholder={index === rows.length - 1 ? placeholder : ''}
          onChange={(e) => {
            const next = [...values];
            next[index] = e.target.value;
            onChange(next.filter((v, i) => v.trim() || i < values.length - 1));
          }}
          data-testid={`${testIdPrefix}-${index}`}
        />
      ))}
    </div>
  );
}

function RateLines({
  rates, onChange, currency, testIdPrefix,
}: {
  rates: { label: string; hourly_rate: string }[];
  onChange: (rates: { label: string; hourly_rate: string }[]) => void;
  currency: string;
  testIdPrefix: string;
}) {
  const rows = [...rates, { label: '', hourly_rate: '' }];
  const set = (index: number, patch: Partial<{ label: string; hourly_rate: string }>) => {
    const next = rows.map((r, i) => (i === index ? { ...r, ...patch } : r));
    onChange(next.filter((r) => r.label.trim() || r.hourly_rate));
  };
  return (
    <div className="space-y-1.5">
      <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Clock className="h-3.5 w-3.5" />
        Hourly rates for anything else
      </p>
      {rows.map((rate, index) => (
        <div key={index} className="grid gap-2 sm:grid-cols-[1fr_190px]">
          <Input
            className="h-8 text-sm"
            value={rate.label}
            placeholder="Normal, nights and weekends…"
            onChange={(e) => set(index, { label: e.target.value })}
            data-testid={`${testIdPrefix}-label-${index}`}
          />
          <AmountField
            compact
            unit={`${currency} / h`}
            value={rate.hourly_rate}
            placeholder="Rate"
            onChange={(value) => set(index, { hourly_rate: value })}
            testId={`${testIdPrefix}-amount-${index}`}
          />
        </div>
      ))}
    </div>
  );
}

function AmountField({
  unit, value, onChange, placeholder, testId, compact = false,
}: {
  unit: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  testId: string;
  compact?: boolean;
}) {
  const height = compact ? 'h-8 text-sm' : '';
  return (
    <div className="flex">
      <Input
        className={`${height} rounded-r-none`}
        type="number"
        min="0"
        step="0.01"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
      <span
        className={`flex items-center whitespace-nowrap rounded-r-md border border-l-0 border-input bg-muted px-2 text-xs text-muted-foreground ${compact ? 'h-8' : ''}`}
        data-testid={`${testId}-unit`}
      >
        {unit}
      </span>
    </div>
  );
}
